import type { DTOFavoriteRequest } from 'src/apis/dto/DTOFavoriteRequest';
import type { DTOFollowPageResponse } from 'src/apis/dto/DTOFollowPageResponse';
import type { DTOFollowRequest } from 'src/apis/dto/DTOFollowRequest';
import type { DTONewFollowerCountResponse } from 'src/apis/dto/DTONewFollowerCountResponse';
import type { DTORestListRequest } from 'src/apis/dto/DTORestListRequest';

import type { APIClientType } from '.';

export type FollowerListFilter = 'ALL' | 'MUTUAL_FOLLOW' | 'ONLINE' | 'LIVE';

export type FollowingListFilter = 'ALL' | 'NEW' | 'FAVORITE' | 'MUTUAL_FOLLOW' | 'ONLINE' | 'LIVE';

export interface FollowServiceType {
  /**
   * 내가 Follow한 사용자 목록 조회
   */
  getFollowingsWithFilter: (
    filter?: FollowingListFilter,
    listRequest?: DTORestListRequest
  ) => Promise<DTOFollowPageResponse>;

  /**
   * 나를 Follow한 사용자 목록 조회
   */
  getFollowersWithFilter: (
    filter?: FollowingListFilter,
    listRequest?: DTORestListRequest
  ) => Promise<DTOFollowPageResponse>;

  /**
   * Follow 요청
   */
  follow: (data: DTOFollowRequest) => Promise<void>;

  /**
   * Unfollow 요청
   */
  unfollow: (data: DTOFollowRequest) => Promise<void>;

  /**
   * 최근 Follower 수 조회 (new 필터와 동일한 기준인 최근 24시간)
   */
  getNewFollowerCount: () => Promise<DTONewFollowerCountResponse>;

  changeFavorite: (data: DTOFavoriteRequest) => Promise<void>;
}

export class FollowService {
  private client: APIClientType;

  constructor(customClient: APIClientType) {
    this.client = customClient;
  }

  public getFollowingsWithFilter = async (
    filter: FollowingListFilter = 'ALL',
    data: DTORestListRequest = {}
  ) => {
    return this.client.call<DTOFollowPageResponse, DTORestListRequest>({
      method: 'GET',
      path: `/api/v1/follow/following/${filter}`,
      data,
    });
  };

  public getFollowersWithFilter = async (
    filter: FollowingListFilter = 'ALL',
    data: DTORestListRequest = {}
  ) => {
    return this.client.call<DTOFollowPageResponse, DTORestListRequest>({
      method: 'GET',
      path: `/api/v1/follow/follower/${filter}`,
      data,
    });
  };

  /**
   * Follow 요청
   */
  public follow = async (data: DTOFollowRequest) => {
    return this.client.call<void, DTOFollowRequest>({
      method: 'POST',
      path: '/api/v1/follow/follow',
      data,
    });
  };

  /**
   * Unfollow 요청
   */
  public unfollow = async (data: DTOFollowRequest) => {
    return this.client.call<void, DTOFollowRequest>({
      method: 'POST',
      path: '/api/v1/follow/unfollow',
      data,
    });
  };

  /**
   * 최근 Follower 수 조회 (new 필터와 동일한 기준인 최근 24시간)
   */
  public getNewFollowerCount = async () => {
    return this.client.call<DTONewFollowerCountResponse, void>({
      method: 'GET',
      path: '/api/v1/follow/follower-new-count',
    });
  };

  public changeFavorite = async (data: DTOFavoriteRequest) => {
    return this.client.call<void, DTOFavoriteRequest>({
      method: 'POST',
      path: '/api/v1/follow/change-favorite',
      data,
    });
  };
}
